'use client';

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LocaleLink } from '@/components/locale-link';
import {
  SavedSearchLimitError,
  useSavedSearches,
} from '@/hooks/use-saved-searches';
import { useAlertSubscriptions } from '@/hooks/use-alerts';
import {
  suggestSearchName,
  type ChipContext,
} from '@/new_components/searchresults/activeFilters';
import type { FilterParams } from '@/lib/search-filter-params';
import { canonicalFilters } from '@/lib/search-filters';
import { FilterChips } from './FilterChips';
import {
  type SavedSearchRef,
  SavedSearchSubscribePrompt,
} from './SavedSearchSubscribePrompt';

type Mode = 'save' | 'capped';

/**
 * Save the current filter set under a name.
 *
 * The dialog previews the exact chips that will appear on the card, which is
 * what removes any ambiguity about *which* filters are being stored — the
 * sidebar holds staged state, and confirming here also applies the search,
 * so what is saved and what is on screen cannot diverge.
 *
 * At the cap it becomes a decision point rather than a wall: a free user is
 * offered "replace <name>" beside "upgrade", because one saved search that
 * cannot be changed is worse than none.
 *
 * Saving is free. Right after a save the subscribe prompt asks whether the
 * user also wants alerts for new matches (priced, credits per day) — unless
 * the replaced search already has one.
 */
export function SaveSearchDialog({
  open,
  onOpenChange,
  filters,
  query,
  sort,
  sortDirection,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: FilterParams;
  /** The executable `CompanySearchParams` payload for the same search. */
  query: Record<string, unknown>;
  sort?: string | null;
  sortDirection?: 'asc' | 'desc';
  onSaved?: (name: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const { items, create, update } = useSavedSearches();
  const { items: alerts } = useAlertSubscriptions();
  const [justSaved, setJustSaved] = useState<SavedSearchRef | null>(null);

  const [name, setName] = useState('');
  const [mode, setMode] = useState<Mode>('save');
  const [capped, setCapped] = useState<SavedSearchLimitError | null>(null);
  const [replaceId, setReplaceId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const suggestion = useMemo(() => {
    const ctx: ChipContext = { t: (k, o) => t(k, o) as string, lang: i18n.language };
    return suggestSearchName(filters, ctx);
  }, [filters, t, i18n.language]);

  // An already-saved identical search is worth saying out loud — otherwise
  // users accumulate duplicates under different names and can't tell them
  // apart on the manage page.
  const duplicate = useMemo(() => {
    const mine = canonicalFilters(filters as unknown as Record<string, unknown>);
    return items.find(
      (s) => canonicalFilters(s.filters) === mine,
    );
  }, [items, filters]);

  // Reset when the dialog opens, adjusted during render rather than in an
  // effect: an effect would paint one frame carrying the previous search's
  // name before correcting itself. (React's documented pattern for state
  // that derives from a prop change.)
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setName(suggestion);
      setMode('save');
      setCapped(null);
      setReplaceId(null);
      setError(null);
    }
  }

  const busy = create.isPending || update.isPending;

  async function handleSave() {
    setError(null);
    const trimmed = name.trim() || suggestion;
    const payload = {
      name: trimmed,
      filters: filters as unknown as Record<string, unknown>,
      query,
      sort: sort ?? null,
      sort_direction: sortDirection ?? 'desc',
    };
    try {
      const saved =
        mode === 'capped' && replaceId != null
          ? await update.mutateAsync({ id: replaceId, patch: payload })
          : await create.mutateAsync(payload);
      onSaved?.(trimmed);
      onOpenChange(false);
      const hasAlert = alerts.some(
        (a) => a.feed_type === 'saved_search' && a.saved_search_id === saved.id,
      );
      if (hasAlert) {
        toast.success(t('subscribeAfterSave.searchSaved'), {
          position: 'top-center',
        });
      } else {
        setJustSaved({ id: saved.id, name: saved.name || trimmed });
      }
    } catch (e) {
      if (e instanceof SavedSearchLimitError) {
        setCapped(e);
        setMode('capped');
        setReplaceId(e.existing[0]?.id ?? null);
        return;
      }
      setError(t('savedSearches.saveFailed', 'Could not save this search. Please try again.'));
    }
  }

  const atCap = mode === 'capped' && capped;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {atCap
                ? t('savedSearches.limitTitle', 'You’ve used all your saved searches')
                : t('savedSearches.saveTitle', 'Save this search')}
            </DialogTitle>
            <DialogDescription>
              {atCap
                ? t('savedSearches.limitDescription', {
                    count: capped!.limit,
                    defaultValue:
                      'Your plan includes {{count}} saved searches. Replace one, or upgrade for more.',
                  })
                : t(
                    'savedSearches.saveDescription',
                    'Come back to these filters any time — we’ll show you how many companies match and how many are new since you last looked.',
                  )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="saved-search-name">
                {t('savedSearches.nameLabel', 'Name')}
              </Label>
              <Input
                id="saved-search-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={255}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !busy) void handleSave();
                }}
              />
            </div>

            <div className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">
                {t('savedSearches.filtersLabel', 'Filters')}
              </span>
              <FilterChips filters={filters} max={12} />
            </div>

            {atCap && capped!.existing.length > 0 && (
              <div className="space-y-2">
                <span className="text-xs font-medium text-muted-foreground">
                  {t('savedSearches.replaceLabel', 'Replace')}
                </span>
                <div className="space-y-1">
                  {capped!.existing.map((s) => (
                    <label
                      key={s.id}
                      className="flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm hover:bg-muted/50"
                    >
                      <input
                        type="radio"
                        name="replace-target"
                        checked={replaceId === s.id}
                        onChange={() => setReplaceId(s.id)}
                      />
                      <span className="truncate">{s.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {!atCap && duplicate && (
              <p className="text-xs text-amber-600 dark:text-amber-500">
                {t('savedSearches.duplicate', {
                  name: duplicate.name,
                  defaultValue: 'You already saved these exact filters as “{{name}}”.',
                })}
              </p>
            )}

            {error && <p className="text-xs text-destructive">{error}</p>}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
              {t('common.buttons.cancel', 'Cancel')}
            </Button>
            {atCap && (
              <Button asChild variant="outline">
                <LocaleLink href="/plans" onClick={() => onOpenChange(false)}>
                  {t('savedSearches.upgradeCta', 'Upgrade')}
                </LocaleLink>
              </Button>
            )}
            <Button onClick={() => void handleSave()} disabled={busy || (!!atCap && replaceId == null)}>
              {busy && <Loader2 className="mr-2 size-4 animate-spin" />}
              {atCap
                ? t('savedSearches.replaceCta', 'Replace')
                : t('savedSearches.saveCta', 'Save search')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <SavedSearchSubscribePrompt
        search={justSaved}
        onClose={() => setJustSaved(null)}
      />
    </>
  );
}
